import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { officialEmail } from "@/lib/employeeContact";
import { formatDisplayDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import AdminLayout from "@/components/AdminLayout";
import EmployeeWizard from "@/components/EmployeeWizard";
import EmployeeView from "@/components/EmployeeView";
import ColumnFilterRow, { applyColumnFilters, columnFiltersActive } from "@/components/ColumnFilterRow";
import {
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  RefreshCw,
  UserPlus,
  Users,
  CheckCircle2,
  PauseCircle,
  Loader2,
  Download,
  FileSpreadsheet,
  FileText as FilePdf,
  FileType,
  UserCog,
  Columns3,
  Filter,
  Upload,
  GripVertical,
  RotateCcw,
  X,
  AlertCircle,
} from "lucide-react";

const STATUS_COLORS = {
  Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Inactive: "bg-slate-200 text-slate-700 border-slate-300",
  Onboarding: "bg-amber-100 text-amber-700 border-amber-200",
  Probation: "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Notice Period": "bg-orange-100 text-orange-700 border-orange-200",
  Resigned: "bg-rose-100 text-rose-700 border-rose-200",
    // NEW STATUSES
  Terminated: "bg-red-600 text-white border-red-700",
  Abscond: "bg-gray-700 text-white border-gray-800",
};

// Default order + visibility for the configurable grid.
// Must match the keys returned by GET /api/exports/employee-fields.
const COLUMN_DEFINITIONS = [
  { key: "code",            label: "Code",            defaultVisible: true,  width: "min-w-[80px]" },
  { key: "photo",           label: "Photo",           defaultVisible: true,  width: "min-w-[60px]" },
  { key: "full_name",       label: "Name",            defaultVisible: true,  width: "min-w-[180px]" },
  { key: "first_name",      label: "First Name",      defaultVisible: false, width: "min-w-[120px]" },
  { key: "middle_name",     label: "Middle Name",     defaultVisible: false, width: "min-w-[120px]" },
  { key: "last_name",       label: "Last Name",       defaultVisible: false, width: "min-w-[120px]" },
  { key: "designation",     label: "Designation",     defaultVisible: true,  width: "min-w-[150px]" },
  { key: "department",      label: "Department",      defaultVisible: true,  width: "min-w-[140px]" },
  { key: "email",           label: "Email",           defaultVisible: true,  width: "min-w-[200px]" },
  { key: "phone",           label: "Phone",           defaultVisible: true,  width: "min-w-[140px]" },
  { key: "employment_type", label: "Type",            defaultVisible: true,  width: "min-w-[100px]" },
  { key: "status",          label: "Status",          defaultVisible: true,  width: "min-w-[100px]" },
  { key: "joining_date",    label: "Joining Date",    defaultVisible: false, width: "min-w-[120px]" },
  { key: "tenure",          label: "Tenure",          defaultVisible: false, width: "min-w-[100px]" },
  { key: "ctc",             label: "CTC (Annual)",    defaultVisible: false, width: "min-w-[120px]" },
  { key: "city",            label: "City",            defaultVisible: false, width: "min-w-[100px]" },
  { key: "state",           label: "State",           defaultVisible: false, width: "min-w-[100px]" },
  { key: "pan",             label: "PAN",             defaultVisible: false, width: "min-w-[110px]" },
  { key: "uan",             label: "UAN",             defaultVisible: false, width: "min-w-[120px]" },
  { key: "bank_name",       label: "Bank Name",       defaultVisible: false, width: "min-w-[140px]" },
  { key: "gender",          label: "Gender",          defaultVisible: false, width: "min-w-[90px]" },
  { key: "dob",             label: "DOB",             defaultVisible: false, width: "min-w-[110px]" },
];
const COLUMN_BY_KEY = Object.fromEntries(COLUMN_DEFINITIONS.map((c) => [c.key, c]));

/** Filter types for directory column filters (must match ColumnFilterRow / applyColumnFilters). */
const EMP_COL_TYPES = {
  photo: "none",
  code: "text",
  full_name: "text",
  first_name: "text",
  middle_name: "text",
  last_name: "text",
  designation: "text",
  department: "select",
  email: "text",
  phone: "text",
  employment_type: "select",
  status: "select",
  joining_date: "date",
  tenure: "number",
  ctc: "number",
  city: "text",
  state: "text",
  pan: "text",
  uan: "text",
  bank_name: "text",
  gender: "select",
  dob: "date",
};

const LS_COL_ORDER = "hrms.employees.colOrder.v1";
const LS_COL_VISIBLE = "hrms.employees.colVisible.v1";

const TENURE_PRESETS = [
  { key: "all",     label: "All Tenures",      min: null, max: null },
  { key: "lt1",     label: "< 1 year",         min: 0, max: 11 },
  { key: "1to3",    label: "1 – 3 years",      min: 12, max: 35 },
  { key: "3to5",    label: "3 – 5 years",      min: 36, max: 59 },
  { key: "5plus",   label: "5+ years",         min: 60, max: null },
  { key: "custom",  label: "Custom range…",    min: null, max: null },
];

function months_between(joiningISO) {
  if (!joiningISO) return 0;
  const j = new Date(joiningISO);
  const now = new Date();
  if (isNaN(j.getTime())) return 0;
  let m = (now.getFullYear() - j.getFullYear()) * 12 + (now.getMonth() - j.getMonth());
  if (now.getDate() < j.getDate()) m -= 1;
  return Math.max(0, m);
}

function tenure_label(m) {
  if (m <= 0) return "< 1 mo";
  const y = Math.floor(m / 12);
  const mm = m % 12;
  if (y === 0) return `${mm} mo`;
  if (mm === 0) return `${y} yr`;
  return `${y} yr ${mm} mo`;
}

export default function Employees() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, onboarding: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("Active"); // ✨ default is Active now
  const [companyFilter, setCompanyFilter] = useState(() => {
    try { return localStorage.getItem("hrms_company_filter") || "all"; } catch { return "all"; }
  });
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ----- Column config (visibility + order), persisted in localStorage -----
  const loadStoredOrder = () => {
    try {
      const v = JSON.parse(localStorage.getItem(LS_COL_ORDER) || "null");
      if (Array.isArray(v) && v.length) {
        // Drop any unknown keys (in case columns were removed in a new version)
        const filtered = v.filter((k) => COLUMN_BY_KEY[k]);
        // Add any new keys at the end
        for (const c of COLUMN_DEFINITIONS) {
          if (!filtered.includes(c.key)) filtered.push(c.key);
        }
        return filtered;
      }
    } catch {
      /* localStorage parse failed — fall through to defaults */
    }
    return COLUMN_DEFINITIONS.map((c) => c.key);
  };
  const loadStoredVisible = () => {
    try {
      const v = JSON.parse(localStorage.getItem(LS_COL_VISIBLE) || "null");
      if (v && typeof v === "object") {
        const result = {};
        for (const c of COLUMN_DEFINITIONS) {
          result[c.key] = c.key in v ? !!v[c.key] : c.defaultVisible;
        }
        return result;
      }
    } catch {
      /* localStorage parse failed — fall through to defaults */
    }
    return Object.fromEntries(COLUMN_DEFINITIONS.map((c) => [c.key, c.defaultVisible]));
  };

  const [colOrder, setColOrder] = useState(loadStoredOrder);
  const [colVisible, setColVisible] = useState(loadStoredVisible);
  useEffect(() => { localStorage.setItem(LS_COL_ORDER, JSON.stringify(colOrder)); }, [colOrder]);
  useEffect(() => { localStorage.setItem(LS_COL_VISIBLE, JSON.stringify(colVisible)); }, [colVisible]);

  const resetColumns = () => {
    setColOrder(COLUMN_DEFINITIONS.map((c) => c.key));
    setColVisible(Object.fromEntries(COLUMN_DEFINITIONS.map((c) => [c.key, c.defaultVisible])));
  };

  // ----- Filter panel state -----
  const [showFilters, setShowFilters] = useState(false);
  const [joiningFrom, setJoiningFrom] = useState("");
  const [joiningTo, setJoiningTo] = useState("");
  const [tenurePreset, setTenurePreset] = useState("all");
  const [tenureMinMo, setTenureMinMo] = useState("");
  const [tenureMaxMo, setTenureMaxMo] = useState("");

  const activeTenure = useMemo(() => {
    if (tenurePreset === "custom") {
      const min = tenureMinMo === "" ? null : Number(tenureMinMo);
      const max = tenureMaxMo === "" ? null : Number(tenureMaxMo);
      return { min, max };
    }
    const p = TENURE_PRESETS.find((x) => x.key === tenurePreset);
    return { min: p?.min ?? null, max: p?.max ?? null };
  }, [tenurePreset, tenureMinMo, tenureMaxMo]);

  const clearFilters = () => {
    setJoiningFrom(""); setJoiningTo("");
    setTenurePreset("all"); setTenureMinMo(""); setTenureMaxMo("");
  };

  const activeFilterCount =
    (joiningFrom || joiningTo ? 1 : 0) +
    (tenurePreset !== "all" ? 1 : 0);

  // ----- Data loading -----
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const empParams = { status_filter: statusFilter, search };
      if (companyFilter && companyFilter !== "all") empParams.company_id = companyFilter;
      try { localStorage.setItem("hrms_company_filter", companyFilter || "all"); } catch { /* ignore */ }
      const [empRes, depRes, desigRes, statsRes] = await Promise.all([
        api.get("/employees", { params: empParams }),
        api.get("/departments"),
        api.get("/designations"),
        api.get("/stats/employees"),
      ]);
      setEmployees(empRes.data);
      setDepartments(depRes.data);
      setDesignations(desigRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error("Failed to load employees data", e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, companyFilter]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const openEmployeeEdit = useCallback(async (emp) => {
    if (!emp?.id) return;
    setEditLoading(true);
    try {
      const res = await api.get(`/employees/${emp.id}`);
      setEditing(res.data);
      setWizardOpen(true);
    } catch (e) {
      console.error("Failed to load employee for edit", e);
      alert(e?.response?.data?.detail || "Could not load employee details for editing");
    } finally {
      setEditLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Sidebar-driven actions (?action=add | bulk-upload | export, ?tab=Intern, ?edit=, ?convert=) ──
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const clearEmployeeActionParams = useCallback(() => {
    if (!searchParams.get("action") && !searchParams.get("edit") && !searchParams.get("convert")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    next.delete("edit");
    next.delete("convert");
    // Keep ?tab=Intern (and any other filters)
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const action = searchParams.get("action");
    const tabParam = searchParams.get("tab");
    const editId = searchParams.get("edit");
    const convertId = searchParams.get("convert");

    // Keep query while modal is open so sidebar active state stays correct.
    if (action === "add" && !wizardOpen) { setEditing(null); setWizardOpen(true); }
    else if (action === "bulk-upload" && !bulkOpen) setBulkOpen(true);
    else if (action === "export" && !exportOpen) setExportOpen(true);

    if (tabParam === "Intern") {
      // Convert Intern view: filter the directory to Intern employment type
      setEmploymentTypeFilter("Intern");
      setStatusFilter("Active");
    } else if (employmentTypeFilter !== "All") {
      // Phase 10 bug fix — when user navigates back to /employees (no tab param),
      // reset the type filter so the full directory loads instead of an empty list.
      setEmploymentTypeFilter("All");
    }

    if (editId && !wizardOpen) {
      const target = employees.find((e) => e.id === editId);
      if (target) openEmployeeEdit(target);
    }
    if (convertId && !convertIntern) {
      const target = employees.find((e) => e.id === convertId);
      if (target) setConvertIntern(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, employees]);

  // Employment-type filter (separate from status filter — used for "Convert Intern" view)
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState("All");
  const [colFilters, setColFilters] = useState({});

  const depMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const desigMap = useMemo(() => Object.fromEntries(designations.map((d) => [d.id, d])), [designations]);

  const empColValue = useCallback((e, key) => {
    switch (key) {
      case "code": return e.basic?.code || "";
      case "full_name": return [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ");
      case "first_name": return e.basic?.first_name || "";
      case "middle_name": return e.basic?.middle_name || "";
      case "last_name": return e.basic?.last_name || "";
      case "designation": return desigMap[e.employment?.designation_id]?.title || "";
      case "department": return depMap[e.employment?.department_id]?.name || "";
      case "email": return officialEmail(e.contact) || "";
      case "phone": return e.contact?.phone || "";
      case "employment_type": return e.employment?.employment_type || "";
      case "status": return e.employment?.status || "";
      case "joining_date": return e.employment?.joining_date || "";
      case "tenure": return months_between(e.employment?.joining_date);
      case "ctc": return Number(e.salary?.annual_ctc || 0);
      case "city": return e.contact?.communication_address?.city || e.contact?.city || "";
      case "state": return e.contact?.communication_address?.state || e.contact?.state || "";
      case "pan": return e.salary?.pan || "";
      case "uan": return e.salary?.uan || "";
      case "bank_name": return e.salary?.bank_name || "";
      case "gender": return e.basic?.gender || "";
      case "dob": return e.basic?.date_of_birth || "";
      default: return "";
    }
  }, [depMap, desigMap]);

  const onColFilterChange = useCallback((key, value) => {
    setColFilters((prev) => ({ ...prev, [key]: value }));
    // Toolbar status_filter controls the API load. Keep it in sync with the Status column
    // filter so picking Probation/etc. is not applied against an Active-only dataset.
    if (key === "status") {
      setStatusFilter(String(value || "").trim() || "All");
    }
    if (key === "employment_type" && String(value || "").trim()) {
      setEmploymentTypeFilter("All");
    }
  }, []);

  // Client-side filtering: search + DoJ range + tenure range + column filters
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = employees.filter((e) => {
      if (s) {
        const full = [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ").toLowerCase();
        const ok =
          full.includes(s) ||
          (e.basic?.code || "").toLowerCase().includes(s) ||
          (officialEmail(e.contact) || "").toLowerCase().includes(s) ||
          (e.contact?.personal_email || "").toLowerCase().includes(s) ||
          (e.contact?.phone || "").toLowerCase().includes(s);
        if (!ok) return false;
      }
      // Phase 7: employment-type filter (used by "Convert Intern" sidebar entry)
      if (employmentTypeFilter && employmentTypeFilter !== "All") {
        if ((e.employment?.employment_type) !== employmentTypeFilter) return false;
      }
      const jd = e.employment?.joining_date || "";
      if (joiningFrom && (!jd || jd < joiningFrom)) return false;
      if (joiningTo && (!jd || jd > joiningTo)) return false;
      const months = months_between(jd);
      if (activeTenure.min !== null && months < activeTenure.min) return false;
      if (activeTenure.max !== null && months > activeTenure.max) return false;
      return true;
    });
    return applyColumnFilters(base, colFilters, empColValue, EMP_COL_TYPES);
  }, [employees, search, joiningFrom, joiningTo, activeTenure, employmentTypeFilter, colFilters, empColValue]);

  // Phase 14 — sortable headers
  const sortAccessors = useMemo(() => ({
    code: (r) => r.basic?.code,
    full_name: (r) => [r.basic?.first_name, r.basic?.middle_name, r.basic?.last_name].filter(Boolean).join(" "),
    email: (r) => officialEmail(r.contact),
    phone: (r) => r.contact?.phone,
    department: (r) => depMap[r.employment?.department_id]?.name || "",
    designation: (r) => desigMap[r.employment?.designation_id]?.title || "",
    employment_type: (r) => r.employment?.employment_type,
    joining_date: (r) => r.employment?.joining_date,
    status: (r) => r.employment?.status,
  }), [depMap, desigMap]);
  const { items: sortedFiltered, sortBy, sortDir, requestSort } = useSortableData(filtered, {
    initialKey: "code",
    accessors: sortAccessors,
  });

  // ----- Actions -----
  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/employees/${confirmDelete.id}`);
      setConfirmDelete(null);
      await loadAll();
    } catch (e) {
      alert("Failed to delete");
    }
  };

  const [statusEdit, setStatusEdit] = useState(null);
  const submitStatusChange = async () => {
    if (!statusEdit) return;
    try {
      await api.patch(`/employees/${statusEdit.emp.id}/status`, { status: statusEdit.next });
      setStatusEdit(null);
      await loadAll();
    } catch (e) {
      alert("Failed to update status");
    }
  };

  // ----- Export modal -----
  const [exportOpen, setExportOpen] = useState(false);

  // ----- Bulk Upload modal -----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [convertIntern, setConvertIntern] = useState(null);

  // ----- Column panel -----
  const [colsOpen, setColsOpen] = useState(false);

  // Visible columns in their stored order
  const visibleCols = useMemo(
    () => colOrder.filter((k) => colVisible[k]).map((k) => COLUMN_BY_KEY[k]).filter(Boolean),
    [colOrder, colVisible],
  );

  const empFilterCols = useMemo(() => visibleCols.map((c) => {
    if (c.key === "photo") return { key: c.key, filterType: "none" };
    if (c.key === "status") {
      return {
        key: c.key,
        filterType: "select",
        options: ["Active", "Onboarding", "Probation", "Notice Period", "Resigned", "Inactive", "Terminated", "Abscond"],
      };
    }
    if (c.key === "employment_type") {
      return { key: c.key, filterType: "select", options: ["Full-time", "Part-time", "Contract", "Intern"] };
    }
    if (c.key === "gender") {
      return { key: c.key, filterType: "select", options: ["Male", "Female", "Other"] };
    }
    if (c.key === "department") {
      return { key: c.key, filterType: "select", options: departments.map((d) => d.name) };
    }
    if (c.key === "joining_date" || c.key === "dob") {
      return { key: c.key, filterType: "date" };
    }
    if (c.key === "ctc" || c.key === "tenure") {
      return { key: c.key, filterType: "number" };
    }
    return { key: c.key, filterType: "text" };
  }), [visibleCols, departments]);

  // ----- Render -----
  return (
    <AdminLayout title="Employees" breadcrumbs={["Home", "HR", "Employees"]}>
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Employees" value={stats.total} color="#226DB4" icon={<Users className="h-6 w-6" />} testid="stat-total" />
        <StatCard label="Active" value={stats.active} color="#089949" icon={<CheckCircle2 className="h-6 w-6" />} testid="stat-active" />
        <StatCard label="Onboarding" value={stats.onboarding} color="#f39c12" icon={<UserPlus className="h-6 w-6" />} testid="stat-onboarding" />
        <StatCard label="Inactive" value={stats.inactive} color="#dd4b39" icon={<PauseCircle className="h-6 w-6" />} testid="stat-inactive" />
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-800">Employee List</h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="employee-search-input"
                placeholder="Search name, code, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded w-64 focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4]"
              />
            </div>

            <select
              data-testid="company-filter-select"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white max-w-[220px]"
            >
              <option value="all">All Companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              data-testid="status-filter-select"
              value={statusFilter}
              onChange={(e) => {
                const next = e.target.value;
                setStatusFilter(next);
                // Avoid fighting the column Status filter when toolbar status is narrowed.
                setColFilters((prev) => (prev.status ? { ...prev, status: "" } : prev));
              }}
              className="px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white"
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Onboarding">Onboarding</option>
              <option value="Probation">Probation</option>
              <option value="Notice Period">Notice Period</option>
              <option value="Resigned">Resigned</option>
              <option value="Inactive">Inactive</option>
              <option value="Terminated">Terminated</option>
              <option value="Abscond">Abscond</option>              
            </select>

            <button
              data-testid="filters-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm border rounded ${
                activeFilterCount > 0
                  ? "border-[#226DB4] bg-[#226DB4]/10 text-[#226DB4]"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Filter className="h-4 w-4" /> Filters
              {activeFilterCount > 0 && (
                <span className="bg-[#226DB4] text-white text-[10px] rounded-full px-1.5 ml-0.5">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              data-testid="columns-btn"
              onClick={() => setColsOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
              title="Configure columns"
            >
              <Columns3 className="h-4 w-4" /> Columns
            </button>

            <button
              data-testid="refresh-btn"
              onClick={loadAll}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 space-y-3" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <Field label="Joining Date — From">
                <input type="date" data-testid="joining-from" value={joiningFrom} onChange={(e) => setJoiningFrom(e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Joining Date — To">
                <input type="date" data-testid="joining-to" value={joiningTo} onChange={(e) => setJoiningTo(e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Tenure">
                <select data-testid="tenure-preset" value={tenurePreset} onChange={(e) => setTenurePreset(e.target.value)} className={fieldCls}>
                  {TENURE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              {tenurePreset === "custom" ? (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="From (months)"><input type="number" min="0" value={tenureMinMo} onChange={(e) => setTenureMinMo(e.target.value)} placeholder="e.g. 6" className={fieldCls} data-testid="tenure-min" /></Field>
                  <Field label="To (months)"><input type="number" min="0" value={tenureMaxMo} onChange={(e) => setTenureMaxMo(e.target.value)} placeholder="e.g. 24" className={fieldCls} data-testid="tenure-max" /></Field>
                </div>
              ) : (
                <div />
              )}
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="text-[11px] text-slate-500">
                {activeFilterCount > 0
                  ? `${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} applied`
                  : "No advanced filters applied"}
              </div>
              <button
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded border border-slate-200 disabled:opacity-50"
                data-testid="clear-filters-btn"
              >
                Clear filters
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto" data-testid="employee-table-wrap">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {visibleCols.map((c) => (
                  sortAccessors[c.key] ? (
                    <SortableTH key={c.key} columnKey={c.key} active={sortBy} dir={sortDir} onClick={requestSort} className={c.width}>
                      {c.label}
                    </SortableTH>
                  ) : (
                    <th key={c.key} className={`text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold ${c.width}`}>{c.label}</th>
                  )
                ))}
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold min-w-[140px]">Actions</th>
              </tr>
              <ColumnFilterRow
                columns={empFilterCols}
                filters={colFilters}
                onChange={onColFilterChange}
                trailingCells={1}
              />
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={6} cols={visibleCols.length + 1} />
              ) : sortedFiltered.length === 0 ? (
                <EmptyState colSpan={visibleCols.length + 1} message="No employees match the current filters" />
              ) : (
                sortedFiltered.map((emp) => (
                  <Row
                    key={emp.id}
                    emp={emp}
                    cols={visibleCols}
                    depMap={depMap}
                    desigMap={desigMap}
                    onView={(e) => navigate(`/employees/${e.id}`)}
                    onEdit={openEmployeeEdit}
                    onStatus={setStatusEdit}
                    onDelete={setConfirmDelete}
                    onConvert={setConvertIntern}
                    canDelete={canDelete}
                    canConvert={user?.role === "admin" || (user?.role_ids || []).includes("role_hr_admin") || (user?.role_ids || []).includes("role_admin")}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 text-xs text-slate-500 border-t border-slate-200 flex items-center gap-3 flex-wrap">
          <span>
            Showing <span className="font-semibold">{filtered.length}</span> of <span className="font-semibold">{employees.length}</span> employees
            {visibleCols.length !== COLUMN_DEFINITIONS.filter((c) => c.defaultVisible).length && (
              <> · <span className="font-semibold">{visibleCols.length}</span> columns visible</>
            )}
          </span>
          {columnFiltersActive(colFilters) && (
            <button
              type="button"
              onClick={() => setColFilters({})}
              className="text-xs text-[#226DB4] hover:underline"
              data-testid="clear-col-filters"
            >
              Clear column filters
            </button>
          )}
        </div>
      </div>

      {wizardOpen && (
        <EmployeeWizard
          key={editing?.id || "new-employee"}
          open={wizardOpen}
          onClose={() => { setWizardOpen(false); setEditing(null); clearEmployeeActionParams(); }}
          onSaved={async () => { setWizardOpen(false); setEditing(null); clearEmployeeActionParams(); await loadAll(); }}
          departments={departments}
          designations={designations}
          companies={companies}
          editing={editing}
          allEmployees={employees}
        />
      )}

      {statusEdit && (
        <StatusEditModal statusEdit={statusEdit} setStatusEdit={setStatusEdit} submit={submitStatusChange} />
      )}

      {viewing && (
        <EmployeeView
          emp={viewing}
          department={depMap[viewing.employment?.department_id]}
          designation={desigMap[viewing.employment?.designation_id]}
          onClose={() => setViewing(null)}
        />
      )}

      {confirmDelete && (
        <DeleteModal emp={confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={onDelete} />
      )}

      {colsOpen && (
        <ColumnsPanel
          colOrder={colOrder}
          setColOrder={setColOrder}
          colVisible={colVisible}
          setColVisible={setColVisible}
          onReset={resetColumns}
          onClose={() => setColsOpen(false)}
        />
      )}

      {exportOpen && (
        <ExportModal
          onClose={() => { setExportOpen(false); clearEmployeeActionParams(); }}
          colOrder={colOrder}
          colVisible={colVisible}
          filterParams={{ statusFilter, search, joiningFrom, joiningTo, ...activeTenure }}
          filteredCount={filtered.length}
          totalCount={employees.length}
        />
      )}

      {bulkOpen && (
        <BulkUploadModal
          onClose={() => { setBulkOpen(false); clearEmployeeActionParams(); }}
          onDone={async () => { setBulkOpen(false); clearEmployeeActionParams(); await loadAll(); }}
        />
      )}

      {convertIntern && (
        <ConvertInternModal
          intern={convertIntern}
          departments={departments}
          designations={designations}
          employees={employees.filter((e) => e.id !== convertIntern.id)}
          onClose={() => { setConvertIntern(null); clearEmployeeActionParams(); }}
          onDone={async () => { setConvertIntern(null); clearEmployeeActionParams(); await loadAll(); }}
        />
      )}
    </AdminLayout>
  );
}

// ============================================================
//  Convert Intern to Permanent — Phase 5
// ============================================================
function ConvertInternModal({ intern, departments, designations, employees, onClose, onDone }) {
  const [form, setForm] = useState({
    new_code: "",
    department_id: intern.employment?.department_id || "",
    designation_id: intern.employment?.designation_id || "",
    employment_type: "Full-time",
    intern_last_working_date: new Date().toISOString().slice(0, 10),   // Phase 10 — required: last day as intern
    joining_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10), // permanent start day
    annual_ctc: 0,
    manager_id: intern.employment?.manager_id || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [codeAuto, setCodeAuto] = useState(true);
  const [codeLoading, setCodeLoading] = useState(false);
  const codeFetchSeq = useRef(0);

  const companyId = intern.company_id || intern.company?.id || "company";

  const fetchNextCode = useCallback(async (employmentType) => {
    const seq = ++codeFetchSeq.current;
    setCodeLoading(true);
    try {
      const r = await api.get("/employees/next-code", {
        params: {
          employment_type: employmentType || "Full-time",
          company_id: companyId,
        },
      });
      if (seq !== codeFetchSeq.current) return;
      const code = r.data?.code || "";
      if (code) {
        setForm((f) => ({ ...f, new_code: code }));
        setCodeAuto(true);
        setErr((e) => (e && String(e).includes("next employee code") ? "" : e));
      } else {
        setErr("Could not load next employee code");
      }
    } catch (e) {
      if (seq !== codeFetchSeq.current) return;
      setErr(e?.response?.data?.detail || "Could not load next employee code");
    } finally {
      if (seq === codeFetchSeq.current) setCodeLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!codeAuto) return;
    fetchNextCode(form.employment_type);
  }, [codeAuto, form.employment_type, fetchNextCode]);

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const code = (form.new_code || "").trim();
      const payload = { ...form };
      if (codeAuto || !code) delete payload.new_code;
      else payload.new_code = code;
      const res = await api.post(`/employees/${intern.id}/convert-to-permanent`, payload);
      setResult(res.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Conversion failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 space-y-4" data-testid="convert-intern-modal">
        <h3 className="text-lg font-semibold text-slate-800">
          Convert Intern → Permanent Employee
        </h3>
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
          <span className="font-semibold">{intern.basic.first_name} {intern.basic.last_name}</span>
          <span className="text-slate-500"> · {intern.basic.code}</span>
        </div>

        {!result ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="New Code (auto from type)">
                <div className="flex gap-2">
                  <input
                    className={fieldCls}
                    value={codeLoading ? "…" : form.new_code}
                    placeholder="e.g. EMP025"
                    disabled={codeLoading}
                    onChange={(e) => { setCodeAuto(false); setForm({ ...form, new_code: e.target.value }); }}
                    data-testid="ci-code"
                  />
                  <button
                    type="button"
                    className="px-2 text-xs border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                    disabled={codeLoading}
                    onClick={() => { setCodeAuto(true); fetchNextCode(form.employment_type); }}
                    data-testid="ci-code-auto"
                  >
                    {codeLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    Auto
                  </button>
                </div>
              </Field>
              <Field label="Last Working Date (as Intern) *">
                <input type="date" required className={fieldCls} value={form.intern_last_working_date}
                  onChange={(e) => setForm({ ...form, intern_last_working_date: e.target.value })} data-testid="ci-intern-lwd" />
              </Field>
              <Field label="Joining Date (as Permanent) *">
                <input type="date" required className={fieldCls} value={form.joining_date}
                  onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="ci-date" />
              </Field>
              <Field label="Department">
                <select className={fieldCls} value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })} data-testid="ci-dept">
                  <option value="">— Select —</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="Designation">
                <select className={fieldCls} value={form.designation_id}
                  onChange={(e) => setForm({ ...form, designation_id: e.target.value })} data-testid="ci-desig">
                  <option value="">— Select —</option>
                  {designations.filter((x) => !form.department_id || x.department_id === form.department_id)
                    .map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </Field>
              <Field label="Employment Type">
                <select
                  className={fieldCls}
                  value={form.employment_type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setCodeAuto(true);
                    setForm({ ...form, employment_type: t });
                    fetchNextCode(t);
                  }}
                  data-testid="ci-type"
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                </select>
              </Field>
              <Field label="Annual CTC (₹)">
                <input type="number" min="0" className={fieldCls} value={form.annual_ctc}
                  onChange={(e) => setForm({ ...form, annual_ctc: Number(e.target.value) })} data-testid="ci-ctc" />
              </Field>
              <Field label="Reporting Manager">
                <select className={fieldCls} value={form.manager_id || ""}
                  onChange={(e) => setForm({ ...form, manager_id: e.target.value })} data-testid="ci-manager">
                  <option value="">— None —</option>
                  {employees.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.basic.first_name} {m.basic.last_name} ({m.basic.code})
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800 space-y-1">
              <div>✓ Personal details, documents and attendance history carry over</div>
              <div>✗ Leave balances reset (no carry-forward)</div>
              <div>📁 Original intern record archived for audit</div>
            </div>
            {err && <div className="text-sm text-rose-600">{err}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
              <button onClick={submit} disabled={busy || !form.department_id || !form.designation_id || !form.intern_last_working_date || !form.joining_date}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium disabled:opacity-50"
                data-testid="ci-submit">
                {busy ? "Converting…" : "Convert & Activate"}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="ci-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
              <div className="font-semibold">✓ Conversion successful</div>
              <div className="text-xs mt-1">
                New employee: <b>{result.new_employee_code}</b> · Original intern record archived.
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onDone} className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium">
                Close & refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  Reusable bits
// ============================================================

const fieldCls = "w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function StatCard({ label, value, color, icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden flex items-stretch">
      <div className="flex items-center justify-center text-white px-5" style={{ backgroundColor: color }}>{icon}</div>
      <div className="px-4 py-3 flex-1">
        <div className="text-2xl font-bold text-slate-800 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}

// One employee table row, only renders the configured columns
function Row({ emp, cols, depMap, desigMap, onView, onEdit, onStatus, onDelete, onConvert, canDelete, canConvert }) {
  const status = emp.employment?.status || "Onboarding";
  const initials = `${emp.basic?.first_name?.[0] || ""}${emp.basic?.last_name?.[0] || ""}`.toUpperCase();
  const dep = depMap[emp.employment?.department_id];
  const desig = desigMap[emp.employment?.designation_id];

  const cellFor = (key) => {
    switch (key) {
      case "code": return emp.basic?.code;
      case "photo":
        return emp.basic?.photo_url ? (
          <img src={emp.basic.photo_url} alt="" className="h-9 w-9 rounded-full object-cover border border-slate-200" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-[#226DB4] text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
        );
      case "full_name":
        return (
          <span className="text-[#226DB4] hover:underline font-medium">
            {[emp.basic?.first_name, emp.basic?.middle_name, emp.basic?.last_name].filter(Boolean).join(" ")}
          </span>
        );
      case "first_name": return emp.basic?.first_name;
      case "middle_name": return emp.basic?.middle_name || "-";
      case "last_name":  return emp.basic?.last_name;
      case "designation":return desig?.title || "-";
      case "department": return dep?.name || "-";
      case "email":      return officialEmail(emp.contact);
      case "phone":      return emp.contact?.phone;
      case "employment_type": return emp.employment?.employment_type;
      case "status": return (
        <span className={`inline-block text-[11px] px-2 py-1 rounded border font-semibold ${STATUS_COLORS[status] || ""}`}>{status}</span>
      );
      case "joining_date": return formatDisplayDate(emp.employment?.joining_date, "-");
      case "tenure": return tenure_label(months_between(emp.employment?.joining_date));
      case "ctc": {
        const n = emp.salary?.annual_ctc || 0;
        return n ? `₹ ${Number(n).toLocaleString("en-IN")}` : "-";
      }
      case "city":      return emp.contact?.communication_address?.city || emp.contact?.city || "-";
      case "state":     return emp.contact?.communication_address?.state || emp.contact?.state || "-";
      case "pan":       return emp.salary?.pan || "-";
      case "uan":       return emp.salary?.uan || "-";
      case "bank_name": return emp.salary?.bank_name || "-";
      case "gender":    return emp.basic?.gender || "-";
      case "dob":       return formatDisplayDate(emp.basic?.date_of_birth, "-");
      default: return "-";
    }
  };

  return (
    <tr
      data-testid={`emp-row-${emp.basic?.code}`}
      onClick={() => onView(emp)}
      className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
    >
      {cols.map((c) => (
        <td key={c.key} className="px-4 py-3 text-slate-700">{cellFor(c.key)}</td>
      ))}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <RowActionsMenu
            testid={`emp-actions-${emp.basic?.code}`}
            items={[
              { label: "View Profile",  icon: Eye,     onClick: () => onView(emp),  testid: `view-emp-${emp.basic?.code}` },
              { label: "Edit",          icon: UserCog, onClick: () => onEdit(emp),  testid: `edit-emp-${emp.basic?.code}` },
              { label: "Change Status", icon: Pencil,  onClick: () => onStatus({ emp, next: status === "Active" ? "Inactive" : status === "Inactive" ? "Active" : "Active" }), testid: `status-edit-${emp.basic?.code}` },
              { label: "Convert to Permanent", icon: UserPlus, onClick: () => onConvert(emp), hidden: !(canConvert && emp.employment?.employment_type === "Intern" && !emp.employment?.is_archived_intern), testid: `convert-intern-${emp.basic?.code}` },
              { label: "Delete", icon: Trash2, danger: true, onClick: () => onDelete(emp), hidden: !canDelete, testid: `delete-emp-${emp.basic?.code}` },
            ]}
          />
        </div>
      </td>
    </tr>
  );
}

// ============================================================
//  Columns panel — drag-to-reorder + show/hide
// ============================================================
function ColumnsPanel({ colOrder, setColOrder, colVisible, setColVisible, onReset, onClose }) {
  const dragKey = useRef(null);

  const handleDragStart = (k) => () => { dragKey.current = k; };
  const handleDragOver = (k) => (e) => {
    e.preventDefault();
    if (!dragKey.current || dragKey.current === k) return;
    const src = dragKey.current;
    setColOrder((prev) => {
      const next = prev.filter((x) => x !== src);
      const dstIdx = next.indexOf(k);
      next.splice(dstIdx, 0, src);
      return next;
    });
  };
  const handleDragEnd = () => { dragKey.current = null; };

  const toggle = (key) => setColVisible((v) => ({ ...v, [key]: !v[key] }));
  const visibleCount = colOrder.filter((k) => colVisible[k]).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" data-testid="columns-panel">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Columns3 className="h-4 w-4 text-[#226DB4]" /> Configure Columns
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {visibleCount} of {colOrder.length} visible · drag to reorder
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <ul className="flex-1 overflow-y-auto p-2" data-testid="columns-list">
          {colOrder.map((key) => {
            const c = COLUMN_BY_KEY[key];
            if (!c) return null;
            const isOn = !!colVisible[key];
            return (
              <li
                key={key}
                draggable
                onDragStart={handleDragStart(key)}
                onDragOver={handleDragOver(key)}
                onDragEnd={handleDragEnd}
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 border border-transparent hover:border-slate-200 cursor-move"
                data-testid={`col-row-${key}`}
              >
                <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(key)}
                  className="rounded"
                  data-testid={`col-toggle-${key}`}
                />
                <span className={`flex-1 text-sm ${isOn ? "text-slate-800" : "text-slate-400"}`}>{c.label}</span>
                <code className="text-[10px] text-slate-400 font-mono">{key}</code>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-between items-center">
          <button onClick={onReset} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50" data-testid="columns-reset-btn">
            <RotateCcw className="h-3.5 w-3.5" /> Reset to default
          </button>
          <button onClick={onClose} className="px-4 py-1.5 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium" data-testid="columns-done-btn">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Export modal
// ============================================================
function ExportModal({ onClose, colOrder, colVisible, filterParams, filteredCount, totalCount }) {
  const [fields, setFields] = useState([]);
  const [useVisible, setUseVisible] = useState(true);
  const [scope, setScope] = useState("filtered"); // 'filtered' | 'all'
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/exports/employee-fields").then((r) => {
      setCatalog(r.data.fields || []);
      // Initialize fields to currently visible columns intersected with the catalog
      const visible = colOrder.filter((k) => colVisible[k] && r.data.fields.some((f) => f.key === k));
      setFields(visible);
    });
  }, [colOrder, colVisible]);

  const allKeys = useMemo(() => catalog.map((c) => c.key), [catalog]);
  const effectiveFields = useMemo(() => {
    if (useVisible) {
      return colOrder.filter((k) => colVisible[k] && allKeys.includes(k));
    }
    return fields;
  }, [useVisible, colOrder, colVisible, fields, allKeys]);

  const trigger = async (format) => {
    if (effectiveFields.length === 0) {
      alert("Pick at least one field to export.");
      return;
    }
    setBusy(true);
    try {
      const params = { fields: effectiveFields.join(",") };
      if (scope === "filtered") {
        if (filterParams.statusFilter && filterParams.statusFilter !== "All") params.status_filter = filterParams.statusFilter;
        if (filterParams.search) params.search = filterParams.search;
        if (filterParams.joiningFrom) params.joining_from = filterParams.joiningFrom;
        if (filterParams.joiningTo) params.joining_to = filterParams.joiningTo;
        if (filterParams.min !== null && filterParams.min !== undefined) params.min_tenure_months = filterParams.min;
        if (filterParams.max !== null && filterParams.max !== undefined) params.max_tenure_months = filterParams.max;
      }
      const res = await api.get(`/exports/employees.${format}`, { params, responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `employees.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      onClose();
    } catch (e) {
      alert("Export failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleField = (k) => {
    setFields((arr) => arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);
  };
  const selectAll = () => setFields(allKeys);
  const selectNone = () => setFields([]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" data-testid="export-modal">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Download className="h-4 w-4 text-[#226DB4]" /> Export Employees
          </h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Rows to export</div>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
              <input type="radio" name="scope" checked={scope === "filtered"} onChange={() => setScope("filtered")} className="mt-0.5" data-testid="export-scope-filtered" />
              <div>
                <div className="text-sm text-slate-800 font-medium">Filtered results ({filteredCount})</div>
                <div className="text-xs text-slate-500">Respects status, search and the Filters panel above</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
              <input type="radio" name="scope" checked={scope === "all"} onChange={() => setScope("all")} className="mt-0.5" data-testid="export-scope-all" />
              <div>
                <div className="text-sm text-slate-800 font-medium">All employees ({totalCount} loaded · server may have more)</div>
                <div className="text-xs text-slate-500">Ignores filters entirely</div>
              </div>
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Fields to export</div>
              <label className="text-xs flex items-center gap-1.5 cursor-pointer text-slate-600">
                <input type="checkbox" checked={useVisible} onChange={(e) => setUseVisible(e.target.checked)} data-testid="export-use-visible" />
                Use currently visible columns
              </label>
            </div>
            {!useVisible && (
              <>
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <button onClick={selectAll} className="px-2 py-1 rounded hover:bg-slate-100 text-slate-600 border border-slate-200" data-testid="export-fields-all">Select all</button>
                  <button onClick={selectNone} className="px-2 py-1 rounded hover:bg-slate-100 text-slate-600 border border-slate-200" data-testid="export-fields-none">Clear</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 border border-slate-200 rounded p-2 max-h-56 overflow-y-auto" data-testid="export-field-picker">
                  {catalog.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={fields.includes(f.key)} onChange={() => toggleField(f.key)} data-testid={`export-field-${f.key}`} />
                      <span className="text-slate-700">{f.label}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            {useVisible && (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2.5">
                Will export <b>{effectiveFields.length}</b> column{effectiveFields.length === 1 ? "" : "s"}:{" "}
                {effectiveFields.map((k) => COLUMN_BY_KEY[k]?.label).filter(Boolean).join(", ") || "(none — turn on some columns first)"}
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50">Cancel</button>
          <button disabled={busy || effectiveFields.length === 0} onClick={() => trigger("csv")} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="export-csv-btn">
            <FileType className="h-4 w-4 text-slate-500" /> CSV
          </button>
          <button disabled={busy || effectiveFields.length === 0} onClick={() => trigger("xlsx")} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="export-xlsx-btn">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
          </button>
          <button disabled={busy || effectiveFields.length === 0} onClick={() => trigger("pdf")} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="export-pdf-btn">
            <FilePdf className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Bulk Upload modal
// ============================================================
function BulkUploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/exports/employees-template.csv", { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "employees-template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert("Failed to download template");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseError("");
    setRows([]);
    setResult(null);
    try {
      const txt = await f.text();
      const parsed = parseCsv(txt);
      setRows(parsed);
    } catch (ex) {
      setParseError(String(ex.message || ex));
    }
  };

  const submit = async () => {
    if (!rows.length) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/employees/bulk-upload", rows);
      setResult(res.data);
    } catch (ex) {
      setResult({ error: ex?.response?.data?.detail || "Upload failed" });
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (result?.created) onDone(); else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" data-testid="bulk-upload-modal">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Upload className="h-4 w-4 text-[#226DB4]" /> Bulk Upload Employees
          </h3>
          <button onClick={close} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
            <div className="font-semibold mb-1">How it works</div>
            <ol className="list-decimal pl-5 text-xs space-y-1">
              <li>Download the CSV template below.</li>
              <li>Fill rows (each row = one new employee). <b>Department</b> & <b>Designation</b> must match existing names exactly.</li>
              <li>Save as CSV and upload here. Existing emails will be skipped.</li>
              <li>Each new employee gets a user account with default password <code className="bg-white px-1 rounded text-[10px]">Welcome@123</code> and must change it on first login.</li>
            </ol>
            <button onClick={downloadTemplate} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-blue-200 rounded text-blue-700 hover:bg-blue-100" data-testid="download-template-btn">
              <Download className="h-3.5 w-3.5" /> Download CSV template
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Select CSV file</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="block w-full text-sm border border-slate-200 rounded px-3 py-2"
              data-testid="bulk-file-input"
            />
            {file && (
              <div className="text-xs text-slate-500 mt-1.5">
                {file.name} · {rows.length} row{rows.length === 1 ? "" : "s"} parsed
              </div>
            )}
            {parseError && (
              <div className="mt-2 bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2 rounded flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5" /> {parseError}
              </div>
            )}
          </div>

          {rows.length > 0 && !result && (
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600">
                Preview (first 5 rows)
              </div>
              <div className="overflow-x-auto max-h-48">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 sticky top-0">
                    <tr>
                      {Object.keys(rows[0]).slice(0, 7).map((k) => (
                        <th key={k} className="text-left px-2.5 py-1.5 font-medium">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {Object.keys(rows[0]).slice(0, 7).map((k) => (
                          <td key={k} className="px-2.5 py-1.5 text-slate-700">{r[k] || "-"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className={`rounded p-3 text-sm border ${result.error ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid="bulk-result">
              {result.error ? (
                <>Error: {result.error}</>
              ) : (
                <>
                  <div className="font-semibold mb-1">
                    Created {result.created} · Skipped {result.skipped}
                  </div>
                  {result.errors?.length > 0 && (
                    <ul className="list-disc pl-5 mt-1 text-xs text-rose-700 space-y-0.5 max-h-32 overflow-y-auto">
                      {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={close} className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50">
            {result?.created ? "Close & refresh" : "Cancel"}
          </button>
          <button
            onClick={submit}
            disabled={busy || rows.length === 0 || result?.created > 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-60"
            data-testid="bulk-submit-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload {rows.length || ""} row{rows.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lightweight CSV parser — handles quoted values, commas inside quotes, "" escaping.
function parseCsv(text) {
  text = text.replace(/^\uFEFF/, ""); // strip BOM
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ",") { cur.push(field); field = ""; }
      else if (ch === "\n" || ch === "\r") {
        if (field !== "" || cur.length) { cur.push(field); rows.push(cur); cur = []; field = ""; }
        if (ch === "\r" && text[i + 1] === "\n") i++;
      } else { field += ch; }
    }
  }
  if (field !== "" || cur.length) { cur.push(field); rows.push(cur); }
  if (rows.length === 0) throw new Error("Empty file");
  const headers = rows[0].map((h) => h.trim());
  const required = ["first_name", "last_name", "email", "department", "designation"];
  const NUMERIC_FIELDS = new Set(["annual_ctc"]);
  for (const r of required) {
    if (!headers.includes(r)) throw new Error(`Missing required column: "${r}"`);
  }
  const out = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (row.every((c) => !c || !c.trim())) continue; // skip blank lines
    const o = {};
    headers.forEach((h, idx) => {
      let v = (row[idx] || "").trim();
      if (NUMERIC_FIELDS.has(h)) v = v ? Number(v) || 0 : 0;
      o[h] = v;
    });
    out.push(o);
  }
  return out;
}

// ============================================================
//  Small modals reused from before
// ============================================================
function StatusEditModal({ statusEdit, setStatusEdit, submit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-slate-800">Change employee status?</h3>
        <p className="text-sm text-slate-600 mt-2">
          You are about to change{" "}
          <span className="font-semibold">
            {statusEdit.emp.basic.first_name} {statusEdit.emp.basic.last_name}
          </span>{" "}
          ({statusEdit.emp.basic.code}) from{" "}
          <span className="font-semibold">{statusEdit.emp.employment.status}</span> to:
        </p>
        <div className="mt-4">
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">New status</label>
          <select
            value={statusEdit.next}
            onChange={(e) => setStatusEdit({ ...statusEdit, next: e.target.value })}
            className={fieldCls}
            data-testid="status-edit-select"
          >
            <option value="Active">Active</option>
            <option value="Onboarding">Onboarding</option>
            <option value="Probation">Probation</option>
          </select>
        </div>
        <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded">
          {statusEdit.next === "Active"
            ? "Activating will include this employee in payroll and active reports."
            : statusEdit.next === "Probation"
            ? "Probation marks the employee as under a trial / observation period."
            : "Onboarding employees are still in pre-joining formalities."}
          <div className="mt-2 text-[11px] text-amber-700">
            To mark an employee as <b>Notice Period</b>, <b>Resigned</b>, <b>Terminated</b>, or <b>Abscond</b>, open the employee profile and use <b>Initiate Exit</b>. This captures the required exit details before finalization.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={() => setStatusEdit(null)} className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="status-edit-cancel-btn">
            Cancel
          </button>
          <button onClick={submit} className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium" data-testid="status-edit-confirm-btn">
            Confirm change
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ emp, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-slate-800">Delete employee?</h3>
        <p className="text-sm text-slate-600 mt-2">
          This will permanently remove{" "}
          <span className="font-semibold">{emp.basic.first_name} {emp.basic.last_name}</span>{" "}
          ({emp.basic.code}). This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="cancel-delete-btn">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white rounded" data-testid="confirm-delete-btn">Delete</button>
        </div>
      </div>
    </div>
  );
}
